import { ExternalLink } from "lucide-react";
import { short, usd, pct, tone } from "@/lib/api";
import { SourceBadges } from "@/components/SourceBadges";

const ago = (ts) => {
  if (!ts) return "unknown";
  const m = Math.max(0, (Date.now() / 1000 - ts) / 60);
  return m < 60 ? `${Math.round(m)}m ago` : m < 2880 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`;
};


export const TradersTable = ({ traders, settings }) => {
  return (
    <div className="panel rise" style={{ animationDelay: "120ms" }} data-testid="traders-table">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-[#1E293B]">
        <div>
          <div className="panel-title" data-testid="traders-title">Top traders · ranked by 30D profit</div>
          <div className="text-xs text-slate-500 mt-1">
            {traders.filter((t) => t.active).length} being copied
            <span className="ml-2 text-[#9945FF]" data-testid="consistency-rule">· {settings.min_win_rate}%+ 30D win rate · active in last 48h · no bots (&gt;2,000 trades) · dropped if our copies fall below {settings.min_win_rate}% after 5</span>
            {settings.safety_checks !== false && <span className="ml-2 text-[#14F195]" data-testid="safety-on">· rug/honeypot filter on</span>}
          </div>
        </div>
        <SourceBadges status={settings.source_status} />
      </div>
      <div className="overflow-auto scroll-thin max-h-[420px]">
        <table className="w-full text-xs font-mono">
          <thead className="sticky top-0 bg-[#0B0F17] text-slate-500 text-[10px] uppercase tracking-wider">
            <tr className="border-b border-[#1E293B]">
              <th className="text-left px-5 py-2.5 font-medium">#</th>
              <th className="text-left px-2 py-2.5 font-medium">Trader</th>
              <th className="text-right px-2 py-2.5 font-medium">PnL</th>
              <th className="text-right px-2 py-2.5 font-medium">Win</th>
              <th className="text-right px-2 py-2.5 font-medium">Txs</th>
              <th className="text-right px-2 py-2.5 font-medium">Our copies</th>
              <th className="text-left px-2 py-2.5 font-medium">Source</th>
              <th className="text-right px-5 py-2.5 font-medium">Copy</th>
            </tr>
          </thead>
          <tbody>
            {traders.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-500" data-testid="traders-empty">{settings.source_status?.gmgn?.ok
                ? `No trader meets the consistency rules right now (${settings.min_win_rate}%+ win rate over 30D, traded in last 48h with 3+ buys/7D, 5+ trades, $${Number(settings.min_pnl_usd).toLocaleString()}+ profit in 30D). Copying pauses until one qualifies.`
                : "No traders yet — scanning sources…"}</td></tr>
            )}
            {traders.map((t, i) => {
              const pnl = t.pnl_usd ?? t.pnl_7d_usd;
              const p = t.pnl_pct ?? t.pnl_7d_pct;
              return (
              <tr key={t.address} className="border-b border-[#1E293B]/60 hover:bg-[#111827] transition-colors" data-testid={`trader-row-${t.address}`}>
                <td className="px-5 py-2.5 text-slate-500">{i + 1}</td>
                <td className="px-2 py-2.5">
                  <a href={`https://gmgn.ai/sol/address/${t.address}`} target="_blank" rel="noreferrer" data-testid={`trader-link-${t.address}`} className="inline-flex items-center gap-1.5 text-slate-200 hover:text-[#14F195] transition-colors">
                    {short(t.address)} <ExternalLink className="w-3 h-3 text-slate-500" />
                  </a>
                  {t.label && <div className="text-[10px] text-slate-500 truncate max-w-[140px]">@{t.label}</div>}
                  <div className="text-[10px] text-slate-500" data-testid={`trader-last-active-${t.address}`}>
                    last trade {ago(t.last_active_ts)}{t.buys_7d ? ` · ${t.buys_7d} buys/7D` : ""}
                  </div>
                </td>
                <td className={`px-2 py-2.5 text-right ${tone(pnl)}`}>
                  {pnl === null || pnl === undefined ? "—" : usd(pnl, 0)}
                  {p ? <div className="text-[10px] opacity-70">{pct(p)}</div> : null}
                </td>
                <td className="px-2 py-2.5 text-right text-slate-300">{t.win_rate ? `${t.win_rate.toFixed(0)}%` : "—"}</td>
                <td className="px-2 py-2.5 text-right text-slate-400">{t.trades ?? t.trades_7d ?? "—"}</td>
                <td className="px-2 py-2.5 text-right" data-testid={`trader-copy-stats-${t.address}`}>
                  {t.copy_closed ? (
                    <>
                      <span className={t.copy_win_rate >= settings.min_win_rate ? "text-[#14F195]" : "text-[#EF4444]"}>{t.copy_win_rate.toFixed(0)}%</span>
                      <div className="text-[10px] text-slate-500">{t.copy_wins}/{t.copy_closed} · {usd(t.copy_pnl, 2)}</div>
                    </>
                  ) : <span className="text-slate-600">—</span>}
                </td>
                <td className="px-2 py-2.5 text-[10px] text-slate-400" data-testid={`trader-source-${t.address}`}>{t.source ? t.source.toUpperCase() : t.manual ? "MANUAL" : "—"}</td>
                <td className="px-5 py-2.5 text-right" data-testid={`trader-status-${t.address}`}>
                  {t.copy_dropped_until * 1000 > Date.now()
                    ? <span className="text-[#EF4444]" title={t.drop_reason}>✕ DROPPED · back {new Date(t.copy_dropped_until * 1000).toLocaleDateString()}</span>
                    : t.active ? <span className="text-[#14F195]">● COPYING</span> : <span className="text-slate-500">○ IDLE</span>}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
